import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { SearchBar } from "@/components/SearchBar";

const HERO_VIDEOS = [0, 1, 2, 3].map((n) => ({
  id: `hero-${n}`,
  poster: `/videos/hero-${n}.jpg`,
  desktop: { mp4: `/videos/hero-${n}.mp4`, webm: `/videos/hero-${n}.webm` },
  mobile: { mp4: `/videos/hero-${n}-mobile.mp4`, webm: `/videos/hero-${n}-mobile.webm` },
}));

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.13 },
  }),
};

const Masked = ({ children, i, className }) => (
  <span className="block overflow-hidden">
    <motion.span variants={line} custom={i} initial="hidden" animate="show" className={`block ${className || ""}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useApp();
  const [active, setActive] = useState(0);
  const [inView, setInView] = useState(true);
  const sectionRef = useRef(null);
  const videoRefs = useRef([]);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Only rotate while the hero is on screen
  useEffect(() => {
    if (!inView) return undefined;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % HERO_VIDEOS.length);
    }, 8000);
    return () => clearInterval(id);
  }, [inView]);

  // Pause every video when the hero scrolls out of view (saves battery/data)
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Play only the active video while in view; pause the rest
  useEffect(() => {
    videoRefs.current.forEach((vid, i) => {
      if (!vid) return;
      if (i === active && inView) {
        const p = vid.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } else {
        vid.pause();
      }
    });
  }, [active, inView, isMobile]);

  return (
    <section ref={sectionRef} className="relative z-20 min-h-[100svh] w-full">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <motion.div
        initial={{ scale: 1.15 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0"
        data-testid="hero-video-bg"
      >
        {HERO_VIDEOS.map((v, i) => {
          const set = isMobile ? v.mobile : v.desktop;
          return (
            <video
              key={`${v.id}-${isMobile ? "m" : "d"}`}
              ref={(el) => (videoRefs.current[i] = el)}
              poster={v.poster}
              muted
              loop
              playsInline
              preload={i === 0 ? "auto" : "metadata"}
              className="absolute inset-0 h-full w-full object-cover transition-opacity duration-[1400ms] ease-in-out"
              style={{ opacity: i === active ? 1 : 0, filter: "brightness(1.14) saturate(1.06) contrast(1.02)" }}
            >
              <source src={set.mp4} type="video/mp4" />
              <source src={set.webm} type="video/webm" />
            </video>
          );
        })}
      </motion.div>
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-petrol/35 via-petrol/10 to-petrol/75" />
      <div className="grain absolute inset-0" />

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-end px-5 pb-10 pt-32 lg:px-8">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="overline mb-6 text-gold"
        >
          {t("hero.overline")}
        </motion.p>

        <h1 className="font-serif text-5xl font-medium leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-8xl">
          <Masked i={0}>{t("hero.title1")}</Masked>
          <Masked i={1}>{t("hero.title2")}</Masked>
          <Masked i={2} className="italic text-terracotta">
            {t("hero.accent")}
          </Masked>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg"
        >
          {t("hero.sub")}
          <span className="font-semibold text-terracotta">{t("hero.sub_save")}</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-9"
        >
          <SearchBar variant="hero" />
        </motion.div>

        <div className="mt-8 flex gap-2" data-testid="hero-video-dots">
          {HERO_VIDEOS.map((v, i) => (
            <button
              key={v.id}
              aria-label={`Video ${i + 1}`}
              data-testid={`hero-video-dot-${i}`}
              onClick={() => setActive(i)}
              className="h-1 rounded-full transition-all duration-500"
              style={{
                width: i === active ? 34 : 14,
                background: i === active ? "#c85a32" : "rgba(255,255,255,0.45)",
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
